import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Stepper,
  Step,
  StepButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { theme } from "../../../shared/theme/theme";

interface StepNavigationProps {
  currentStep: number;
  totalSteps: number;
  onStepClick: (stepIndex: number) => void;
  stepTitles: string[];
  completedSteps: Set<number>;
}

export const StepNavigation: React.FC<StepNavigationProps> = ({
  currentStep,
  totalSteps,
  onStepClick,
  stepTitles,
  completedSteps,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stepperRef = useRef<HTMLDivElement>(null);

  const handleStepClick = (stepIndex: number) => {
    // Only allow navigation to current step or completed steps
    if (stepIndex <= currentStep || completedSteps.has(stepIndex)) {
      onStepClick(stepIndex);
    }
  };

  // Auto-scroll to active step
  useEffect(() => {
    if (!isMobile && scrollContainerRef.current && stepperRef.current) {
      const scrollContainer = scrollContainerRef.current;
      const stepper = stepperRef.current;

      // Find the active step element
      const activeStepElement = stepper.querySelector(
        `[data-step="${currentStep}"]`
      ) as HTMLElement;

      if (activeStepElement) {
        const containerWidth = scrollContainer.clientWidth;
        const stepLeft = activeStepElement.offsetLeft;
        const stepWidth = activeStepElement.clientWidth;
        const scrollLeft = scrollContainer.scrollLeft;

        // Calculate if step is visible
        const isVisible =
          stepLeft >= scrollLeft &&
          stepLeft + stepWidth <= scrollLeft + containerWidth;

        if (!isVisible) {
          // Scroll to center the active step
          const scrollToPosition =
            stepLeft - containerWidth / 2 + stepWidth / 2;
          scrollContainer.scrollTo({
            left: Math.max(0, scrollToPosition),
            behavior: "smooth",
          });
        }
      }
    }
  }, [currentStep, isMobile]);

  return (
    <Box
      sx={{
        width: "100%",
        mb: 4,
        p: 2,
        pt: 8,
        bgcolor: "background.paper",
        borderRadius: 1,
        boxShadow: 1,
      }}
    >
      {/* <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
        Application Progress
      </Typography> */}

      {isMobile ? (
        // Mobile: Show current step info
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {t("account.leaseApplication.step")} {currentStep + 1}{" "}
            {t("account.leaseApplication.of")} {totalSteps}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: "medium", mt: 1 }}>
            {stepTitles[currentStep]}
          </Typography>
          <Box
            sx={{
              width: "100%",
              height: 4,
              bgcolor: "grey.200",
              borderRadius: 2,
              mt: 2,
            }}
          >
            <Box
              sx={{
                width: `${((currentStep + 1) / totalSteps) * 100}%`,
                height: "100%",
                bgcolor: "primary.main",
                borderRadius: 2,
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        </Box>
      ) : (
        // Desktop: Show full stepper with horizontal scroll
        <Box
          sx={{
            width: "100%",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            ref={scrollContainerRef}
            sx={{
              overflowX: "auto",
              overflowY: "hidden",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            <Stepper
              ref={stepperRef}
              activeStep={currentStep}
              alternativeLabel
              sx={{
                minWidth: "max-content",
                px: 1,
                "& .MuiStepConnector-line": {
                  borderTopWidth: 2,
                },
                "& .MuiStep-root": {
                  minWidth: 120, // Ensure minimum width for each step
                },
              }}
            >
              {stepTitles.map((title, index) => {
                const isCompleted = completedSteps.has(index);
                const isClickable = index <= currentStep || isCompleted;

                return (
                  <Step key={index} completed={isCompleted} data-step={index}>
                    <StepButton
                      onClick={() => handleStepClick(index)}
                      sx={{
                        cursor: isClickable ? "pointer" : "not-allowed",
                        "& .MuiStepLabel-label": {
                          fontSize: "0.75rem",
                          fontWeight: index === currentStep ? "bold" : "normal",
                          color: isClickable ? "inherit" : "text.disabled",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 120,
                        },
                        "& .MuiStepIcon-root": {
                          color: isCompleted
                            ? "success.main"
                            : index === currentStep
                            ? "primary.main"
                            : isClickable
                            ? "action.active"
                            : "action.disabled",
                        },
                      }}
                      disabled={!isClickable}
                    >
                      {title}
                    </StepButton>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
        </Box>
      )}
    </Box>
  );
};
